import './ProductPage.scss';

import { b2x } from '@b2x/react/src';
import classnames from 'classnames';
import React from 'react';

import { Accordion } from '../Accordion';
import { useAppContext } from '../AppContext';
import { ButtonVariant } from '../Button';
import { MiscellaneousContentType, ProductPromoContentType, SizeGuideContentType } from '../contentTypes';
import { CustomRadio } from '../CustomRadio';
import { t } from '../i18n/i18n';
import { Icon, IconName, IconSize } from '../Icon';
import animazioneCampanella from '../images/product/animazione-campanella.json';
import { ProductTile } from '../ProductTile';
import { RecommendedProductsA } from '../RecommendedProductsA';
import { FooterNewsletter } from './FooterNewsletter';
import { Page } from './Page';

export interface ProductPagePros {}

export const ProductPage = (props: ProductPagePros) => {
  const product = b2x.useProduct({ populate: b2x.appConfig.api?.productPopulate });

  const [variantsDivContainer, scrollToVariantsDivContainer] = b2x.useScrollTo(-180);

  const { isFooterCopyrightVisible } = useAppContext();

  const addToCartContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [isOutOfViewTopValue, setIsOutOfViewTopValue] = React.useState(false);

  const isOutOfViewTop = React.useCallback(() => {
    if (!addToCartContainerRef.current) return false;
    const rect = addToCartContainerRef.current.getBoundingClientRect();
    return rect.top < 0;
  }, []);

  React.useEffect(() => {
    const handleScroll = () => {
      setIsOutOfViewTopValue(isOutOfViewTop());
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isOutOfViewTop]);

  const { session } = b2x.useAppContext();

  return (
    <Page className="product-page" noPaddingBottom noPaddingTop thingsToLoadBeforePageReady={[product]}>
      {product !== undefined && (
        <b2x.ProductContext product={product}>
          {(productContext) => {
            const sizeChartAttr = productContext.selectedProductVariant.attributes?.find(
              (attr) => attr.typeCode === 'ESZ_SIZECHARTTABLE'
            )?.value;

            const recommendedProductsCode = productContext.selectedProductVariant.attributes?.find(
              (attr) => attr.typeCode === 'ESZ_RECOMMENDED_PRODUCT_CONTENT'
            )?.value;

            return (
              <React.Fragment>
                <b2x.structuredData.Product product={product} selectedSku={productContext.selectedSku} />
                <section className="first-section mb-5 mb-xl-6">
                  <b2x.Container fluid="always">
                    <b2x.Breadcrumb hideLastItem />
                    <b2x.Row className="custom-row">
                      <b2x.Col size={{ lg: 6, xs: 12 }}>
                        <div className="position-relative">
                          <ProductGallery
                            product={product}
                            selectedProductVariant={productContext.selectedProductVariant}
                            selectedSku={
                              productContext.selectedSku ?? productContext.selectedProductVariant.skus?.at(0)
                            }
                          />
                        </div>
                      </b2x.Col>
                      <b2x.Col size={{ lg: 6, xs: 12 }}>
                        <b2x.Div
                          className="info-column-inner-container"
                          paddingStart={{ lg: 4, xl: 5 }}
                          paddingTop={{ xl: 5, xs: 4 }}
                        >
                          <b2x.Div marginBottom={{ lg: 4, xl: 5, xs: 3 }}>
                            {productContext.selectedProductVariant.name && (
                              <h1 className="product-title h1 mb-2 mb-lg-3">
                                {b2x.formatHtml(productContext.selectedProductVariant.name.toUpperCase())}
                              </h1>
                            )}
                            {product.descriptionShort && (
                              <p className="h5">{productContext.selectedProductVariant.descriptionShort}</p>
                            )}
                          </b2x.Div>

                          <b2x.AddToCartFormHelper className="mb-4" product={product} scope="product">
                            {({ fieldsHelper, formik, priceHelper, selectedProductVariant, selectedSku }) => {
                              const productColor = selectedProductVariant.attributes?.find(
                                (attribute) => attribute.typeCode === 'ESZ_COLORE'
                              )?.value;

                              const variantLabel = productColor
                                ? `${t('misc.product.color')}: ${productColor}`
                                : t('misc.product.color');

                              const productSize = selectedSku?.attributes?.find(
                                (attribute) => attribute.typeCode === 'ESZ_MISURA'
                              )?.value;

                              const skuLabel = selectedSku
                                ? `${t('misc.product.size')}: ${productSize}`
                                : t('misc.product.size');

                              const selectedColorAttr = selectedProductVariant.attributes?.find(
                                (attr) => attr.typeCode === 'ESZ_COLORE'
                              );
                              const selectedSizeAttr = selectedSku?.attributes?.find(
                                (attr) => attr.typeCode === 'ESZ_MISURA'
                              );

                              const stickyFooterAttributes: Array<b2x.AttributeApiDto> | undefined = [];
                              selectedColorAttr && stickyFooterAttributes.push(selectedColorAttr);
                              selectedSizeAttr && stickyFooterAttributes.push(selectedSizeAttr);
                              return (
                                <React.Fragment>
                                  {fieldsHelper.productVariantsFieldSet.productVariants.formFields.length > 0 && (
                                    <b2x.Div className="product-variants" innerRef={variantsDivContainer}>
                                      <fieldset>
                                        <legend className="visually-hidden">
                                          {fieldsHelper.productVariantsFieldSet.legend}
                                        </legend>
                                        <b2x.FormGroup
                                          {...fieldsHelper.productVariantsFieldSet.productVariants.formGroup}
                                          className="mb-4"
                                          label={variantLabel}
                                          noMarginBottom
                                        >
                                          <b2x.Div display={'flex'} gap={3}>
                                            {fieldsHelper.productVariantsFieldSet.productVariants.formFields.map(
                                              ({ productVariant, radio }) => {
                                                const label =
                                                  productVariant.attributes?.find(
                                                    (attribute) => attribute.typeCode === 'ESZ_COLORE'
                                                  )?.value ?? 'Attributo variante mancante';

                                                const productVariantImage = productVariant.skus?.at(0)?.image
                                                  ? productVariant.skus.at(0)?.image?.src
                                                  : productVariant.image?.src;

                                                const image = {
                                                  alt: label,
                                                  title: label,
                                                  url: productVariantImage ?? '',
                                                };

                                                return (
                                                  <CustomRadio
                                                    image={image}
                                                    key={radio.id}
                                                    label={label}
                                                    radio={radio}
                                                    type="image"
                                                  />
                                                );
                                              }
                                            )}
                                          </b2x.Div>
                                        </b2x.FormGroup>
                                      </fieldset>
                                    </b2x.Div>
                                  )}
                                  {fieldsHelper.skusFieldSet.skus.formFields.length > 1 && (
                                    <b2x.Div className="product-skus">
                                      <fieldset>
                                        <legend className="visually-hidden">{fieldsHelper.skusFieldSet.legend}</legend>
                                        <b2x.FormGroup
                                          {...fieldsHelper.skusFieldSet.skus.formGroup}
                                          className="size-selector"
                                          label={skuLabel}
                                        >
                                          {sizeChartAttr && <SizeGuide sizeChartContentCode={sizeChartAttr} />}
                                          <b2x.Div className="size-selector-options" display={'flex'} gap={2}>
                                            {fieldsHelper.skusFieldSet.skus.formFields.map(({ radio, sku }) => {
                                              const label =
                                                sku.attributes?.find((attribute) => attribute.typeCode === 'ESZ_MISURA')
                                                  ?.value ?? 'Attributo sku mancante';
                                              return (
                                                <CustomRadio key={radio.id} label={label} radio={radio} type="text" />
                                              );
                                            })}
                                          </b2x.Div>
                                        </b2x.FormGroup>
                                      </fieldset>
                                    </b2x.Div>
                                  )}
                                  <ProductPrice priceHelper={priceHelper} />
                                  <b2x.Div display={{ lg: 'block', xs: 'grid' }} innerRef={addToCartContainerRef}>
                                    <b2x.AddToCartFormButton<ButtonVariant, IconName, IconSize>
                                      fieldsHelper={fieldsHelper}
                                      product={product}
                                      selectedSku={selectedSku}
                                      submitButton={{ iconEnd: { name: 'cart', size: 24 } }}
                                    />
                                  </b2x.Div>
                                  <ProductPromoCode attributes={product.attributes} />

                                  {session?.cart?.shippingProfile?.description && (
                                    <b2x.Div className="small hstack gap-2" marginTop={{ lg: 3, xs: 2 }}>
                                      <b2x.Span className="bg-gray-100 p-1 rounded-pill">
                                        <Icon name="fast-delivery" size={24} />
                                      </b2x.Span>
                                      {t('misc.product.shippingDescription', {
                                        shippingDescription: session.cart.shippingProfile.description,
                                      })}
                                    </b2x.Div>
                                  )}
                                  <b2x.Portal>
                                    <b2x.ProductStickyFooter
                                      fieldsHelper={fieldsHelper}
                                      formik={formik}
                                      isVisible={isOutOfViewTopValue && !isFooterCopyrightVisible}
                                      priceHelper={priceHelper}
                                      product={product}
                                      scrollToElement={scrollToVariantsDivContainer}
                                      selectedProductAttributes={stickyFooterAttributes}
                                      selectedProductImage={selectedProductVariant.image}
                                      selectedSku={selectedSku}
                                      submitDesktopButton={{
                                        iconEnd: { name: 'cart', size: 24 },
                                        label:
                                          selectedSku?.state === 'AVAILABLE'
                                            ? t('form.addToCartForm.buttons.submit.label')
                                            : t('misc.productNotAvailable'),
                                        variant: 'primary',
                                      }}
                                      submitMobileButton={{
                                        iconEnd: { name: 'cart', size: 24 },
                                        justifyContent: 'center',
                                        label:
                                          selectedSku?.state === 'AVAILABLE'
                                            ? t('form.addToCartForm.buttons.submit.label')
                                            : t('misc.productNotAvailable'),
                                        variant: 'primary',
                                      }}
                                    />
                                  </b2x.Portal>
                                  {b2x.appConfig.installmentPaymentBanner && (
                                    <div className="my-3">
                                      <b2x.InstallmentPaymentBanner amount={priceHelper.price} />
                                    </div>
                                  )}
                                </React.Fragment>
                              );
                            }}
                          </b2x.AddToCartFormHelper>
                          <ProductInfo product={productContext.selectedProductVariant} />
                        </b2x.Div>
                      </b2x.Col>
                    </b2x.Row>
                  </b2x.Container>
                </section>
                <b2x.RelatedProducts productId={productContext.selectedProductVariant.id}>
                  {({ fetching, relatedProducts }) =>
                    fetching ? (
                      <b2x.Loading />
                    ) : (
                      relatedProducts &&
                      relatedProducts.length > 0 && (
                        <section className="second-section mb-5 mb-xl-6">
                          <b2x.EqualHeight>
                            <b2x.Container>
                              <h3 className={classnames('h1 text-center mb-4 mb-lg-5')}>
                                {t('product.relatedProduct.title').toUpperCase()}
                              </h3>

                              <b2x.Listing
                                name="Product page - Related products"
                                products={relatedProducts}
                                sendEvent={false}
                              >
                                <b2x.Row>
                                  {relatedProducts.map((_product) => (
                                    <b2x.Col key={_product.id} size={{ md: 4, xs: 12 }}>
                                      <ProductTile product={_product} />
                                    </b2x.Col>
                                  ))}
                                </b2x.Row>
                              </b2x.Listing>
                            </b2x.Container>
                          </b2x.EqualHeight>
                        </section>
                      )
                    )
                  }
                </b2x.RelatedProducts>
                {recommendedProductsCode && <RecommendedProductsA contentCode={recommendedProductsCode} />}
              </React.Fragment>
            );
          }}
        </b2x.ProductContext>
      )}
      <b2x.Div className="bg-gray-100 box-newsletter py-5">
        <FooterNewsletter />
      </b2x.Div>
    </Page>
  );
};

interface ProductInfoProps {
  product: b2x.ProductApiDto;
}

const ProductInfo = ({ product }: ProductInfoProps) => {
  const currentBreakpoint = b2x.useBreakpoint();
  const switchFrom = 'sm';

  const _productDescription = {
    content: b2x.formatHtml(`${product.description}<div class="mt-4 text-gray-300 small">Cod: ${product.code}</div>`),
    id: 'product-description',
    title: t('product.description'),
  };

  const curaProdottoBottom = product.attributes?.find(
    (attribute) => attribute.typeCode === 'ESZ_CURADELPRODOTTO_BOTTOM'
  )?.value;

  const _productAttributes =
    product.attributes
      ?.filter(
        (attribute) => attribute.typeCode === 'ESZ_DETTAGLIPRODOTTO' || attribute.typeCode === 'ESZ_CURADELPRODOTTO'
      )
      .map((attribute) => ({
        content:
          attribute.typeCode === 'ESZ_CURADELPRODOTTO' ? (
            <>
              {b2x.formatHtml(attribute.value)}
              <b2x.Div marginY={3}>
                {product.attributes?.map(
                  (attributeIcon, index) =>
                    attributeIcon.typeCode &&
                    attributeIcon.typeCode.startsWith('ESZ_ICON_') && (
                      <>
                        <b2x.Span paddingEnd={2}>
                          <Icon
                            name={
                              attributeIcon.typeCode
                                .substring(9, attributeIcon.typeCode.length)
                                .toLowerCase() as IconName
                            }
                            size={36}
                          />
                        </b2x.Span>
                      </>
                    )
                )}
              </b2x.Div>
              {curaProdottoBottom && b2x.formatHtml(curaProdottoBottom)}
            </>
          ) : (
            b2x.formatHtml(attribute.value)
          ),
        id: attribute.typeCode,
        title: attribute.title ?? '',
      })) ?? [];

  const _productInfo: Array<{ content: string | React.ReactElement; id: string; title: string }> = [];
  _productInfo.push(_productDescription);
  _productAttributes.length > 0 && _productInfo.push(..._productAttributes);

  return (
    <b2x.Div className="product-info">
      {b2x.untilBreakpoint(switchFrom, currentBreakpoint) ? (
        <Accordion id="product-info-accordion" itemBodyClassName="pt-0">
          {_productInfo.map((accordionItem) => (
            <b2x.AccordionItem
              headingClass="h3"
              headingTag="h2"
              id={accordionItem.id}
              key={accordionItem.id}
              title={accordionItem.title}
            >
              {accordionItem.content}
            </b2x.AccordionItem>
          ))}
        </Accordion>
      ) : (
        <b2x.Tabs
          className="gap-4"
          contentClassName="pt-4 pb-xl-5"
          headingClass="h3"
          headingTag="h2"
          id="product-info-tab"
          tabStyle="tabs"
          tabs={_productInfo.map((tabItem) => ({
            children: tabItem.content,
            title: tabItem.title,
          }))}
        />
      )}
    </b2x.Div>
  );
};

interface ProductPriceProps {
  priceHelper: b2x.UsePriceResult;
}

const ProductPrice = ({ priceHelper }: ProductPriceProps) => {
  return (
    <b2x.Div className="product-price-container" display="flex" flexDirection="column" gap={2} marginY={3}>
      <div>
        <b2x.PriceBlock
          alignItems="center"
          classNames={{
            discountPercentage: 'bg-primary text-white px-2',
            price: 'h3',
          }}
          gap={2}
          hiddenDiscountPercentage
          priceHelper={priceHelper}
        />
      </div>
      {b2x.appConfig.enableBestPrice && priceHelper.bestPriceValue && (
        <div className="extra-small">
          <b2x.BestPrice priceHelper={priceHelper} />
        </div>
      )}
    </b2x.Div>
  );
};

interface ProductGalleryProps {
  product: b2x.ProductApiDto;
  selectedProductVariant: b2x.ProductApiDto;
  selectedSku?: b2x.SkuApiDto;
}

const ProductGallery = ({ product, selectedProductVariant, selectedSku }: ProductGalleryProps) => {
  const slides = b2x.useGallery(selectedProductVariant, selectedSku).slides;

  const currentBreakpoint = b2x.useBreakpoint();

  return (
    <div className="product-gallery">
      <b2x.Lightbox>
        <b2x.SwiperContext>
          {(swiper) => (
            <b2x.Swiper
              direction={b2x.untilBreakpoint('lg', currentBreakpoint) ? 'horizontal' : 'vertical'}
              pagination
              slides={slides.map(({ src, type, uId }, index) => (
                <b2x.Div className="slide-container" key={uId}>
                  {type === 'image' ? (
                    <b2x.ImageSwitcher
                      alt={product.name}
                      aspectRatio={b2x.appConfig.productImageAspectRatio}
                      fluid
                      src={src}
                    />
                  ) : (
                    swiper.swiper?.activeIndex === index && (
                      <b2x.Video
                        options={{
                          autoplay: swiper.swiper?.activeIndex === index,
                          controls: true,
                          loop: true,
                          muted: true,
                          pip: false,
                          playsinline: true,
                        }}
                        variants={{ xs: { height: 1080, source: { external: { file: src } }, width: 1920 } }}
                      />
                    )
                  )}
                </b2x.Div>
              ))}
            />
          )}
        </b2x.SwiperContext>
      </b2x.Lightbox>
    </div>
  );
};

interface SizeGuideProps {
  sizeChartContentCode: string;
}

const SizeGuide = ({ sizeChartContentCode }: SizeGuideProps) => {
  const { showModal } = b2x.useModals();
  const sizeChartContent = b2x.useContent<SizeGuideContentType>(sizeChartContentCode);

  const handleButtonClick = React.useCallback(() => {
    sizeChartContent &&
      showModal({
        bodyClassName: 'size-guide-modal-content-wrapper',
        children: <SizeGuideModal sizeChartContent={sizeChartContent} />,
        fullscreen: 'lg',
        headerClassName: 'size-guide-modal-header',
        size: 'large',
        title: sizeChartContent.body.title ?? t('product.sizeGuide'),
      });
  }, [showModal, sizeChartContent]);

  return (
    <b2x.Div className="size-guide">
      {sizeChartContent && (
        <b2x.Button
          className="text-decoration-underline"
          label={t('product.sizeGuide')}
          onClick={handleButtonClick}
          variant="blank"
        />
      )}
    </b2x.Div>
  );
};

interface SizeGuideModalProps {
  sizeChartContent: b2x.ContentApiDto<SizeGuideContentType>;
}

const SizeGuideModal = ({ sizeChartContent }: SizeGuideModalProps) => {
  return (
    <b2x.Div className="size-guide-modal-content">
      {sizeChartContent.body.asset && (
        <b2x.Div marginBottom={4}>
          <b2x.AssetV2 {...sizeChartContent.body.asset} fluid />
        </b2x.Div>
      )}
      {sizeChartContent.body.table && (
        <b2x.Div className="table-responsive lh-1">{b2x.formatHtml(sizeChartContent.body.table)}</b2x.Div>
      )}
    </b2x.Div>
  );
};

interface ProductPromoCodePros {
  attributes?: Array<b2x.AttributeApiDto>;
}

const ProductPromoCode = ({ attributes }: ProductPromoCodePros) => {
  // Recupero il valore dall'attributo impostato.
  const productPromoCodeContent = attributes?.find(
    (attribute) => attribute.typeCode === 'ESZ_PROMO_CONTENT_CODE'
  )?.value;

  const miscContent = b2x.useContent<MiscellaneousContentType>('MISCELLANEOUS_CONTENT');
  // Se presente un valore, recupero il contenuto del banner.
  const content = productPromoCodeContent && b2x.useContent<ProductPromoContentType>(productPromoCodeContent);
  const { showModal } = b2x.useModals();

  const codeRef = React.useRef<HTMLSpanElement>(null);
  const handleCopyCodeButtonClick = React.useCallback(() => {
    const couponCode = codeRef.current?.innerText ?? '';
    navigator.clipboard.writeText(couponCode).then((response) => {
      showModal({
        autoClose: 2,
        centered: true,
        children: miscContent?.body.productPage?.couponCodeBanner?.contentModal,
        className: 'modal-copy-coupon',
        closable: false,
        title: miscContent?.body.productPage?.couponCodeBanner?.titleModal || '',
      });
    });
  }, [
    miscContent?.body.productPage?.couponCodeBanner?.contentModal,
    miscContent?.body.productPage?.couponCodeBanner?.titleModal,
    showModal,
  ]);

  return (
    <>
      {typeof content === 'object' && content.body.backgroundColor && (
        <div
          className="product-promo-code my-4 p-3"
          style={{
            background: content.body.backgroundColor,
          }}
        >
          <b2x.Row className="align-items-center" gap={3}>
            <b2x.Col size={'auto'}>
              {content.body.icon ? (
                <Icon
                  className=""
                  name={content.body.icon as IconName}
                  size={26}
                  style={{ color: content.body.iconColor }}
                />
              ) : (
                content.body.showLottie && (
                  <b2x.LottieElement className="ringbell" src={animazioneCampanella as unknown as string} />
                )
              )}
            </b2x.Col>
            <b2x.Col>
              <div className="" style={{ color: content.body.textColor }}>
                {b2x.formatHtml(content.body.description)}
              </div>
              <div className="small my-1">
                {content.body.couponCode && <span className="text-uppercase"> {t('misc.code')}: </span>}
                <span className="fw-bold" ref={codeRef} style={{ color: content.body.couponCodeColor }}>
                  {b2x.formatHtml(content.body.couponCode)}
                </span>
              </div>
            </b2x.Col>

            <b2x.Col size={{ sm: 'auto', xs: 12 }}>
              <div className="pb-3 pb-sm-0 text-center">
                {content.body.ctaCoupon?.label ? (
                  <b2x.Button
                    label={content.body.ctaCoupon.label}
                    onClick={handleCopyCodeButtonClick}
                    variant={content.body.ctaCoupon.variant}
                  />
                ) : (
                  content.body.cta?.label && <b2x.CtaFromContent {...content.body.cta} />
                )}
              </div>
            </b2x.Col>
          </b2x.Row>
        </div>
      )}
    </>
  );
};
